import { Component, OnInit } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { InsuranceLookupService } from '../_services/insuranceLookup.service';
import { forkJoin } from 'rxjs';
import { map } from 'rxjs/operators';
import { FileConversionServiceService } from '../_services/file-conversion-service.service';
import { environment } from 'src/environments/environment';

declare var $: any;

@Component({
  selector: 'app-pre-auth-discharge',
  templateUrl: './pre-auth-discharge.component.html',
  styleUrls: ['./pre-auth-discharge.component.css']
})
export class PreAuthDischargeComponent {

  submitted : boolean = false;
  preAuthForm: FormGroup;
  preAuthSubmitted : boolean = false;
  dosageSubmitted : boolean = false;
  diagnosisSubmitted : boolean = false;
  procedureSubmitted : boolean = false;
  supportingInfoSubmitted : boolean = false;
  patientId : any = ''
  careTeamSubmitted : boolean  = false;

  preAuthSent : boolean = false;

  quantityUnits: string[] = ['tablets', 'capsules', 'vials', 'mL', 'g', 'mcg', 'units', 'packs', 'ampoules', 'drops', 'sprays'];

  doctorDropdownList : any = [];
  hospitalDropdownList : any = []

  awsBaseUrl : string  = environment.media_domain;

  constructor(private insuranceLookupService: InsuranceLookupService,
    private formBuilder: FormBuilder,
    private spinner: NgxSpinnerService,
    private router: Router,
    private toastr: ToastrService,
    private route: ActivatedRoute,
    private fileConversionService : FileConversionServiceService) {

    this.preAuthForm = this.formBuilder.group({
      billStartDate: ["", Validators.required],
      billEndDate: ["", Validators.required],
      items: this.formBuilder.array([this.createItem()]),
      totalAmount : 0,
      dosageInstructions : this.formBuilder.array([this.createDosageArray()]),
      diagnosis : this.formBuilder.array([this.createDiagnosisArray()]),
      procedure : this.formBuilder.array([this.createProcedureArray()]),
      supportingInfo : this.formBuilder.array([this.createSupportingInfoArray()]),
      hospitalId : ['' , Validators.required],
      doctorId : ['' , Validators.required],
      careTeam: this.formBuilder.array([this.createCareTeamArray()])
    })

        // Using observable
        this.route.paramMap.subscribe(params => {
          this.patientId = params.get('id');
        });



  }

  ngOnInit() {
    this.getDoctorDropdownList()
    this.getHospitalDropdownList()
    this.getPreAuthData()
  }


  get items(): FormArray {
    return this.preAuthForm.get('items') as FormArray;
  }

  get dosageInstructions(): FormArray {
    return this.preAuthForm.get('dosageInstructions') as FormArray;
  }

  get diagnosis(): FormArray {
    return this.preAuthForm.get('diagnosis') as FormArray;
  }

  get procedure(): FormArray {
    return this.preAuthForm.get('procedure') as FormArray;
  }

  get supportingInfo(): FormArray {
    return this.preAuthForm.get('supportingInfo') as FormArray;
  }

  get careTeam(): FormArray {
    return this.preAuthForm.get('careTeam') as FormArray;
  }

  getDoctorDropdownList() {
    this.spinner.show()
    this.insuranceLookupService.getDoctorsDropdown(
    ).subscribe({
      next: (data) => {
        this.spinner.hide()
        this.doctorDropdownList = data.data
      },
      error: (error) => {
        this.toastr.error(error.status.message)
        this.spinner.hide()
      }
    })
   }
  
   getHospitalDropdownList() {
    this.spinner.show()
    this.insuranceLookupService.getHospitalDropdown(
    ).subscribe({
      next: (data) => {
        
        this.spinner.hide()
        this.hospitalDropdownList = data.data
      },
      error: (error) => {
        this.toastr.error(error.status.message)
        this.spinner.hide()
      }
    })
   }



  createItem(): FormGroup {
    const itemGroup = this.formBuilder.group({
      itemCode: ['', Validators.required],
      itemName: ['', Validators.required],
      serviceDate: ['', Validators.required],
      quantityValue: ['', [Validators.required, Validators.min(1)]],
      quantityUnit: ['', Validators.required],
      unitPrice: ['', [Validators.required, Validators.min(0)]],
      netPrice: ['' , Validators.required]
    });

    // Listen for changes to unitPrice and quantityValue to calculate netPrice
    itemGroup.get('unitPrice')!.valueChanges.subscribe(() => {
      this.calculateNetPrice(itemGroup);
    });
    itemGroup.get('quantityValue')!.valueChanges.subscribe(() => {
      this.calculateNetPrice(itemGroup);
    });

    return itemGroup;
  }

  createDosageArray() : FormGroup {
    const dosageGroup = this.formBuilder.group({
      dosage : ['' , Validators.required],
      additionalInstructionCode : ['' , Validators.required],
      additionalInstruction: ['', Validators.required],
      timingFrequency: ['', Validators.required],
      timingPeriod: ['', Validators.required],
      periodUnit: ['', Validators.required],
      route: ['', Validators.required],
      routeCode : ['', Validators.required],
      method: ['', Validators.required],
      methodCode : ['', Validators.required]
    })
    return dosageGroup
  }

  createDiagnosisArray() : FormGroup {
    const diagnosisGroup = this.formBuilder.group({
      diagnosisCodeableConceptCode:  ['', Validators.required],
      diagnosisCodeableConceptDisplay:  ['', Validators.required],
      diagnosisCode: ['', Validators.required],
      diagnosis: ['', Validators.required],
    })
    return diagnosisGroup
  }

  createProcedureArray() : FormGroup {
    const procedureGroup = this.formBuilder.group({
      procedureCode: ['', Validators.required],
      procedure: ['', Validators.required],
    })
    return procedureGroup
  }

  createSupportingInfoArray() : FormGroup {
    const supportingInfoGroup = this.formBuilder.group({
      documentType: [''],
      documentName : ['' , Validators.required],
      treatment: ['', Validators.required],
    })
    return supportingInfoGroup
  }

  createCareTeamArray() : FormGroup {
    const careTeamGroup = this.formBuilder.group({
      roleCode: ['', Validators.required],
      roleDisplay: ['', Validators.required],
      qualificationCode: ['', Validators.required],
      qualificationDisplay: ['', Validators.required],
    })
    return careTeamGroup
  }


  calculateNetPrice(itemGroup: FormGroup): void {
    const unitPrice = itemGroup.get('unitPrice')!.value;
    const quantityValue = itemGroup.get('quantityValue')!.value;
    const netPrice = unitPrice * quantityValue;
    itemGroup.get('netPrice')!.setValue(netPrice, { emitEvent: false });
    this.calculateTotalPrice();
  }

  calculateTotalPrice(): void {
    const totalPrice = this.items.controls.reduce((sum, item) => {
      return sum + (item.get('netPrice')!.value || 0);
    }, 0);

    this.preAuthForm.patchValue({
      totalAmount : totalPrice 
    })
  }

  getErrorMessage(controlName: string, formGroup: FormGroup | AbstractControl , fieldName : string , submitted : boolean): string {
    const control = formGroup.get(controlName);
    if (!control) return '';
    if (control.hasError('required') && (submitted || control.touched)) {
      return `${fieldName} is required`;
    }
    return '';
  }

  submitPreAuth(){

    this.submitted = true;
    this.preAuthSubmitted = true;
    this.dosageSubmitted = true;
    this.diagnosisSubmitted = true;
    this.procedureSubmitted = true;
    this.supportingInfoSubmitted = true;

    if (this.preAuthForm.invalid) {
      return;
    }

    this.submitPreAuthorization()
  }

  addItem(): void {
    this.preAuthSubmitted = true;
    if (this.items.valid) {
      this.preAuthSubmitted = false;
      this.items.push(this.createItem());
    } else {
      this.preAuthSubmitted = false;
      this.items.markAllAsTouched();  
    }
  }

  addDosage() : void {
    this.dosageSubmitted = true;
    if (this.dosageInstructions.valid) {
      this.dosageSubmitted = false;
      this.dosageInstructions.push(this.createDosageArray());
    } else {
      this.dosageSubmitted = false;
      this.dosageInstructions.markAllAsTouched();  
    }
  }

  addDiagnosis() : void {
    this.diagnosisSubmitted = true;
    if (this.diagnosis.valid) {
      this.diagnosisSubmitted = false;
      this.diagnosis.push(this.createDiagnosisArray());
    } else {
      this.diagnosisSubmitted = false;
      this.diagnosis.markAllAsTouched();  
    }
  }

  addProcedure() : void {
    this.procedureSubmitted = true;
    if (this.procedure.valid) {
      this.procedureSubmitted = false;
      this.procedure.push(this.createProcedureArray());
    } else {
      this.procedureSubmitted = false;
      this.procedure.markAllAsTouched();  
    }
  }

  addSupporting() : void {
    this.supportingInfoSubmitted = true;
    if (this.supportingInfo.valid) {
      this.supportingInfoSubmitted = false;
      this.supportingInfo.push(this.createSupportingInfoArray());
    } else {
      this.supportingInfoSubmitted = false;
      this.supportingInfo.markAllAsTouched();  
    }
  }

  addCareTeam() : void {
    this.careTeamSubmitted = true;
    if (this.careTeam.valid) {
      this.careTeamSubmitted = false;
      this.careTeam.push(this.createCareTeamArray());
    } else {
      this.careTeamSubmitted = false;
      this.careTeam.markAllAsTouched();  
    }
  }

  removeItem(index: number): void {
    this.items.removeAt(index);
  }

  removeDosage(index: number): void {
    this.dosageInstructions.removeAt(index);
  }

  removeDiagnosis(index: number): void {
    this.diagnosis.removeAt(index);
  }

  removeProcedure(index: number): void {
    this.procedure.removeAt(index);
  }

  removeSuportingInfo(index: number): void {
    this.supportingInfo.removeAt(index);
  }

  removeCareTeam(index: number): void {
    this.careTeam.removeAt(index);
  }


  onFileChange(event: Event, index: number): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = reader.result as string;
        this.supportingInfo!.at(index)!.get('documentType')!.setValue(base64);
      };
      reader.readAsDataURL(file);
    }
  }

  submitPreAuthorization() {
 
   this.spinner.show()
  
  //  this.insuranceLookupService.submitPreAuth(
  //   this.patientId,
  //   this.preAuthForm.value.billStartDate,
  //   this.preAuthForm.value.billEndDate,
  //   this.preAuthForm.value.items,
  //   this.preAuthForm.value.totalAmount,
  //   this.preAuthForm.value.dosageInstructions,
  //   this.preAuthForm.value.supportingInfo,
  //   this.preAuthForm.value.diagnosis,
  //   this.preAuthForm.value.procedure,
  //   this.preAuthForm.value.hospitalId,
  //   this.preAuthForm.value.doctorId,
  //   "final",
  //   "14",
  //   this.preAuthForm.value.careTeam,
  //  ).subscribe({
  //    next: (data) => {
  //      this.spinner.hide()
  //      $("#preAuthSubmittedModal").modal("show");
  //      this.preAuthSent = true;
  //    },
  //    error: (error) => {
  //      this.toastr.error(error.status.message)
  //      this.spinner.hide()
  //    }
  //  })
 }

//  getPreAuthData() {
 
//   this.spinner.show()
 
//   this.insuranceLookupService.getPreAuthData(
//    this.patientId,
//   ).subscribe({
//     next: (data) => {
//       this.preAuthForm.patchValue({
//         ...data.data[0],
//         supportingInfo : [
//           {
//             documentType: '',
//             documentName : '',
//             treatment: ''
//           }
//         ]
//       })
      
//       this.spinner.hide()
//     },
//     error: (error) => {
//       this.toastr.error(error.status.message)
//       this.spinner.hide()
//     }
//   })
// }

async getPreAuthData() {
  this.spinner.show();

  try {
    const data = await this.insuranceLookupService.getPreAuthData(this.patientId).toPromise();
    const supportingInfo = data.data[0].supportingInfo;

    const base64Observables = await Promise.all(supportingInfo.map(async (info : any) => {
      const base64String = await this.fileConversionService.getBase64FromUrl(this.awsBaseUrl + info.documentType);
      return { ...info, documentType: base64String };
    }));

    // this.preAuthForm.patchValue({
    //   ...data.data[0],
    //   supportingInfo: base64Observables
    // });

    this.spinner.hide();
  } catch (error : any) {
    console.error('Error fetching pre-auth data:', error);
    this.toastr.error(error.message || 'An error occurred');
    this.spinner.hide();
  }
}


 track(){
  $("#preAuthSubmittedModal").modal("hide");
  this.router.navigate([`/claim-tracker/view-record/${this.patientId}`])
 }

 back(){
  this.router.navigate(['/insurance-lookup'])
 }
}
